'use client';

import { c as _c } from "react/compiler-runtime";
import type { PeripheralBalanceType } from '@morpho-org/blue-sdk';
import type { Amount, InterfaceToken } from '@morpho-org/dapp-core';
import { ZERO } from '@morpho-org/dapp-core';
import { format, isDefined } from '@morpho-org/morpho-ts';
import { Avatar, WarningPopover } from '@morpho-org/ui-v6/client';
import { Skeleton } from '@morpho-org/ui-v6/server';
import { cn, formatters, getDigitsFromPriceAndAmount } from '@morpho-org/ui-v6/utils';
import NumberFlow from '@number-flow/react';
import { Button, Icon, Tooltip, TooltipContent, TooltipTrigger, TruncatedTooltip } from '@repo/ui';
import { Alert, Close } from '@repo/ui/icons';
import { type InputHTMLAttributes, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PeripheralTokenOption } from '../types';
import PeripheralSourcesStack from '../PeripheralSourcesStack';
import { useAssetInput } from '../useAssetInput';
function toUsdNumber(token: InterfaceToken, amount: Amount) {
  const number = Number(format.number.digits(2).trailingZero(false).locale('en-US').of(token.toUsd(amount ?? ZERO), 18));
  if (isNaN(number)) throw new Error('Invalid number');
  return number;
}
interface CompactAssetInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  className?: string;
  header: string;
  token: InterfaceToken;
  amount: Amount;
  onChange: (value: bigint) => void;
  onPendingChange?: (isPending: boolean) => void;
  maxValue?: Amount;
  isMax?: boolean;
  debouncedTime?: number;
  disabled?: boolean;
  peripheralTokenOptions?: PeripheralTokenOption[];
  onPeripheralTokenChange?: (id: PeripheralBalanceType, isActive: boolean) => void;
  error?: string | ReactNode;
  onMaxClick?: (value: boolean) => void;
  onClose?: () => void;
  islastInput?: boolean;
  fullWidth?: boolean;
  actionSlot?: ReactNode;
  maxTooltip?: string;
  loading?: boolean;
}
const CompactAssetInput = t0 => {
  const $ = _c(122);
  const {
    className,
    header,
    token,
    debouncedTime,
    amount,
    maxValue,
    isMax,
    disabled,
    peripheralTokenOptions,
    error,
    islastInput,
    fullWidth,
    actionSlot,
    maxTooltip,
    loading,
    onChange,
    onPendingChange,
    onMaxClick,
    onClose,
    onPeripheralTokenChange,
    autoFocus
  } = t0;
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);
  let t1;
  if ($[0] !== amount || $[1] !== debouncedTime || $[2] !== onChange || $[3] !== token.decimals) {
    t1 = {
      value: amount,
      decimals: token.decimals,
      debouncedTime,
      onChange
    };
    $[0] = amount;
    $[1] = debouncedTime;
    $[2] = onChange;
    $[3] = token.decimals;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const {
    handleChange,
    valueStr,
    isPending,
    flush
  } = useAssetInput(t1);
  let t2;
  let t3;
  if ($[5] !== isPending || $[6] !== onPendingChange) {
    t2 = () => {
      onPendingChange?.(isPending);
      return () => onPendingChange?.(false);
    };
    t3 = [isPending, onPendingChange];
    $[5] = isPending;
    $[6] = onPendingChange;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
  const isInputDisabled = disabled;
  let t4;
  if ($[9] !== isInputDisabled || $[10] !== isMax || $[11] !== maxValue || $[12] !== onMaxClick) {
    t4 = () => {
      if (!isInputDisabled && maxValue != null) {
        onMaxClick?.(!isMax);
      }
    };
    $[9] = isInputDisabled;
    $[10] = isMax;
    $[11] = maxValue;
    $[12] = onMaxClick;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const handleMaxClick = t4;
  let t5;
  if ($[14] !== maxValue || $[15] !== token.decimals || $[16] !== token.price || $[17] !== token.symbol) {
    t5 = formatters.short.unit(token.symbol).digits(getDigitsFromPriceAndAmount(token.price, maxValue)).trailingZero(!maxValue).of(maxValue ?? ZERO, token.decimals);
    $[14] = maxValue;
    $[15] = token.decimals;
    $[16] = token.price;
    $[17] = token.symbol;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const maxLabel = t5;
  let t6;
  if ($[19] !== amount || $[20] !== token) {
    t6 = formatters.short.unit("$").digits(2).trailingZero(false).of(token.toUsd(amount ?? ZERO), 18);
    $[19] = amount;
    $[20] = token;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const amountPriceUsd = t6;
  let t7;
  try {
    let t8;
    if ($[22] !== amount || $[23] !== token) {
      t8 = toUsdNumber(token, amount);
      $[22] = amount;
      $[23] = token;
      $[24] = t8;
    } else {
      t8 = $[24];
    }
    t7 = t8;
  } catch {
    t7 = undefined;
  }
  const usdNumber = t7;
  const isPeripheralsDisplayed = peripheralTokenOptions && peripheralTokenOptions.length > 0;
  const t8 = fullWidth && "w-full";
  let t9;
  if ($[25] !== className || $[26] !== t8) {
    t9 = cn("gap-xs flex flex-col", t8, className);
    $[25] = className;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== flush) {
    t10 = e => {
      if (e.target !== inputRef.current && document.activeElement === inputRef.current) {
        flush();
        e.preventDefault();
      }
    };
    $[28] = flush;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== header) {
    t11 = headerRef => <span ref={headerRef} className="text-2xs text-secondary-foreground max-w-[320px] min-w-0 truncate md:shrink-0">{header}</span>;
    $[30] = header;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== header || $[33] !== t11) {
    t12 = <TruncatedTooltip text={header}>{t11}</TruncatedTooltip>;
    $[32] = header;
    $[33] = t11;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== loading) {
    t13 = loading && <Skeleton className="h-[26px] w-[100px]" />;
    $[35] = loading;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== handleMaxClick || $[38] !== isInputDisabled || $[39] !== isMax || $[40] !== loading || $[41] !== maxLabel || $[42] !== maxTooltip || $[43] !== maxValue || $[44] !== onMaxClick || $[45] !== token.decimals || $[46] !== token.symbol) {
    t14 = onMaxClick && !loading ? <div className="gap-xs flex flex-row items-center md:min-w-0">{maxValue ? <Tooltip><TooltipTrigger asChild={true}><span tabIndex={0} className="text-3xs text-secondary-foreground max-w-[90px] min-w-0 truncate whitespace-nowrap md:max-w-none">{maxLabel}</span></TooltipTrigger><TooltipContent>{formatters.short.unit(token.symbol).digits(8).trailingZero(false).of(maxValue, token.decimals)}</TooltipContent></Tooltip> : <TruncatedTooltip text={maxLabel}>{maxLabelRef => <span ref={maxLabelRef} className="text-3xs text-secondary-foreground max-w-[90px] min-w-0 truncate whitespace-nowrap md:max-w-none">{maxLabel}</span>}</TruncatedTooltip>}<Tooltip><TooltipTrigger asChild={true}><Button variant="secondary" size="sm" className="h-[26px]" data-testid="asset-input-max" disabled={isInputDisabled || isMax || !maxValue} onClick={handleMaxClick}>MAX</Button></TooltipTrigger>{!(!maxTooltip || isMax) && <TooltipContent>{maxTooltip}</TooltipContent>}</Tooltip></div> : null;
    $[37] = handleMaxClick;
    $[38] = isInputDisabled;
    $[39] = isMax;
    $[40] = loading;
    $[41] = maxLabel;
    $[42] = maxTooltip;
    $[43] = maxValue;
    $[44] = onMaxClick;
    $[45] = token.decimals;
    $[46] = token.symbol;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  let t15;
  if ($[48] !== islastInput || $[49] !== onClose) {
    t15 = onClose && (islastInput ? <WarningPopover headerLabel="Remove transaction from Bundler?" body="Closing this modal will remove this transaction from the Bundler." ctaLabel="Remove from Bundler" cancelLabel="Cancel" ctaAction={onClose}><Button variant="ghost" size="iconSm" aria-label="Remove"><Close /></Button></WarningPopover> : <Button variant="ghost" size="iconSm" onClick={onClose} aria-label="Remove"><Close /></Button>);
    $[48] = islastInput;
    $[49] = onClose;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  let t16;
  if ($[51] !== t13 || $[52] !== t14 || $[53] !== t15) {
    t16 = <div className="gap-xs flex items-center md:min-w-0">{t13}{t14}{t15}</div>;
    $[51] = t13;
    $[52] = t14;
    $[53] = t15;
    $[54] = t16;
  } else {
    t16 = $[54];
  }
  let t17;
  if ($[55] !== actionSlot || $[56] !== t16) {
    t17 = <div className="gap-xs flex flex-row items-center md:min-w-0">{actionSlot}{t16}</div>;
    $[55] = actionSlot;
    $[56] = t16;
    $[57] = t17;
  } else {
    t17 = $[57];
  }
  let t18;
  if ($[58] !== t12 || $[59] !== t17) {
    t18 = <div className="md:gap-sm flex flex-row items-center justify-between">{t12}{t17}</div>;
    $[58] = t12;
    $[59] = t17;
    $[60] = t18;
  } else {
    t18 = $[60];
  }
  const t19 = focused && "outline-ring outline-1";
  const t20 = isInputDisabled ? "cursor-not-allowed" : "cursor-text";
  let t21;
  if ($[61] !== t19 || $[62] !== t20) {
    t21 = cn("gap-sm p-md flex h-[87px] rounded-[20px]", "bg-muted", t19, t20);
    $[61] = t19;
    $[62] = t20;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  let t22;
  if ($[64] !== isInputDisabled) {
    t22 = () => !isInputDisabled && inputRef.current?.focus();
    $[64] = isInputDisabled;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  let t23;
  if ($[66] !== handleChange || $[67] !== isInputDisabled || $[68] !== isMax || $[69] !== onMaxClick) {
    t23 = e_0 => {
      if (!isInputDisabled) {
        handleChange(e_0.target.value);
      }
      if (isMax) {
        onMaxClick?.(false);
      }
    };
    $[66] = handleChange;
    $[67] = isInputDisabled;
    $[68] = isMax;
    $[69] = onMaxClick;
    $[70] = t23;
  } else {
    t23 = $[70];
  }
  let t24;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => {
      setFocused(true);
    };
    $[71] = t24;
  } else {
    t24 = $[71];
  }
  let t25;
  if ($[72] !== flush) {
    t25 = () => {
      flush();
      setFocused(false);
    };
    $[72] = flush;
    $[73] = t25;
  } else {
    t25 = $[73];
  }
  let t26;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = cn("text-md text-foreground h-[33px] w-full border-none bg-transparent p-0 outline-none", "caret-primary truncate", "placeholder:text-muted-foreground disabled:cursor-not-allowed");
    $[74] = t26;
  } else {
    t26 = $[74];
  }
  let t27;
  if ($[75] !== autoFocus || $[76] !== isInputDisabled || $[77] !== t23 || $[78] !== t25 || $[79] !== valueStr) {
    t27 = <input ref={inputRef} aria-label="Asset Input" placeholder="0.00" value={valueStr} onChange={t23} onFocus={t24} onBlur={t25} disabled={isInputDisabled} inputMode="decimal" lang="en-US" data-testid="asset-input" autoFocus={autoFocus} className={t26} />;
    $[75] = autoFocus;
    $[76] = isInputDisabled;
    $[77] = t23;
    $[78] = t25;
    $[79] = valueStr;
    $[80] = t27;
  } else {
    t27 = $[80];
  }
  const t28 = amount ? "text-secondary-foreground" : focused && !isInputDisabled ? "text-secondary-foreground" : "text-muted-foreground";
  let t29;
  if ($[81] !== t28) {
    t29 = cn("text-2xs truncate leading-none break-all", t28);
    $[81] = t28;
    $[82] = t29;
  } else {
    t29 = $[82];
  }
  let t30;
  if ($[83] !== amountPriceUsd || $[84] !== usdNumber) {
    t30 = isDefined(usdNumber) ? <><span className="hidden md:inline"><NumberFlow value={usdNumber ?? 0} prefix="$" locales="en-US" format={{
          notation: "compact",
          compactDisplay: "short",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          trailingZeroDisplay: "stripIfInteger"
        }} /></span><span className="inline md:hidden">{amountPriceUsd}</span></> : amountPriceUsd;
    $[83] = amountPriceUsd;
    $[84] = usdNumber;
    $[85] = t30;
  } else {
    t30 = $[85];
  }
  let t31;
  if ($[86] !== t29 || $[87] !== t30) {
    t31 = <span className={t29}>{t30}</span>;
    $[86] = t29;
    $[87] = t30;
    $[88] = t31;
  } else {
    t31 = $[88];
  }
  let t32;
  if ($[89] !== t27 || $[90] !== t31) {
    t32 = <div className="flex grow flex-col">{t27}{t31}</div>;
    $[89] = t27;
    $[90] = t31;
    $[91] = t32;
  } else {
    t32 = $[91];
  }
  let t33;
  if ($[92] !== token.logoURI) {
    t33 = <Avatar src={token.logoURI} size="small" />;
    $[92] = token.logoURI;
    $[93] = t33;
  } else {
    t33 = $[93];
  }
  const t34 = token.symbol ?? "";
  let t35;
  if ($[94] !== token.symbol) {
    t35 = symbolRef => <span ref={symbolRef} className="text-md text-foreground max-w-[70px] truncate leading-none">{token.symbol}</span>;
    $[94] = token.symbol;
    $[95] = t35;
  } else {
    t35 = $[95];
  }
  let t36;
  if ($[96] !== t34 || $[97] !== t35) {
    t36 = <TruncatedTooltip text={t34}>{t35}</TruncatedTooltip>;
    $[96] = t34;
    $[97] = t35;
    $[98] = t36;
  } else {
    t36 = $[98];
  }
  let t37;
  if ($[99] !== t33 || $[100] !== t36) {
    t37 = <div className="gap-2xs flex items-center">{t33}{t36}</div>;
    $[99] = t33;
    $[100] = t36;
    $[101] = t37;
  } else {
    t37 = $[101];
  }
  let t38;
  if ($[102] !== t21 || $[103] !== t22 || $[104] !== t32 || $[105] !== t37) {
    t38 = <div className={t21} onClick={t22}>{t32}{t37}</div>;
    $[102] = t21;
    $[103] = t22;
    $[104] = t32;
    $[105] = t37;
    $[106] = t38;
  } else {
    t38 = $[106];
  }
  let t39;
  if ($[107] !== isInputDisabled || $[108] !== isPeripheralsDisplayed || $[109] !== onPeripheralTokenChange || $[110] !== peripheralTokenOptions || $[111] !== token) {
    t39 = isPeripheralsDisplayed && peripheralTokenOptions && <PeripheralSourcesStack token={token} peripheralTokenOptions={peripheralTokenOptions} isInputElementDisabled={isInputDisabled} onPeripheralTokenChange={onPeripheralTokenChange} />;
    $[107] = isInputDisabled;
    $[108] = isPeripheralsDisplayed;
    $[109] = onPeripheralTokenChange;
    $[110] = peripheralTokenOptions;
    $[111] = token;
    $[112] = t39;
  } else {
    t39 = $[112];
  }
  let t40;
  if ($[113] !== error) {
    t40 = error && <div className="gap-2xs flex items-center justify-end"><Icon icon={<Alert />} className="text-destructive-foreground" />{typeof error === "string" ? <span className="text-2xs text-destructive-foreground leading-none">{error}</span> : error}</div>;
    $[113] = error;
    $[114] = t40;
  } else {
    t40 = $[114];
  }
  let t41;
  if ($[115] !== t10 || $[116] !== t18 || $[117] !== t38 || $[118] !== t39 || $[119] !== t40 || $[120] !== t9) {
    t41 = <div className={t9} onMouseDown={t10}>{t18}{t38}{t39}{t40}</div>;
    $[115] = t10;
    $[116] = t18;
    $[117] = t38;
    $[118] = t39;
    $[119] = t40;
    $[120] = t9;
    $[121] = t41;
  } else {
    t41 = $[121];
  }
  return t41;
};
export default CompactAssetInput;